import {
  parseSpeakerTurns,
  SPEAKER_ROLES,
  type SpeakerAssignment,
  type SpeakerRole,
} from "@rosc/contracts";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import type { JSX } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  V3_MENU_CONTENT,
  V3_MENU_ITEM,
  V3_MENU_LABEL,
  V3_MENU_SEPARATOR,
} from "@/components/ui/v3/menu";
import { useVisitSpeakers } from "@/hooks/visits/VisitSpeakers";
import { flagsInTurn } from "@/lib/review/transcriptFlags";
import { cn } from "@/lib/utils";
import { FlaggedText, useTranscriptFlagsFor } from "./TranscriptFlags";
import { useTranscriptV3 } from "./transcript-style";

const ROLE_STYLES: Record<SpeakerRole | "unset", string> = {
  clinician:
    "bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-400/15 dark:text-sky-200 dark:ring-sky-400/30",
  other_clinician:
    "bg-indigo-50 text-indigo-800 ring-indigo-200 dark:bg-indigo-400/15 dark:text-indigo-200 dark:ring-indigo-400/30",
  patient:
    "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-200 dark:ring-emerald-400/30",
  caregiver:
    "bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/30",
  interpreter:
    "bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-400/15 dark:text-violet-200 dark:ring-violet-400/30",
  other_patient:
    "bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-200 dark:ring-rose-400/30",
  other:
    "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-400/15 dark:text-slate-200 dark:ring-slate-400/30",
  unknown: "bg-muted text-muted-foreground ring-border",
  unset: "bg-muted text-muted-foreground ring-border",
};
// v3.2: each role keeps its own tint, as on the classic path (#258), drawn
// from the v3 status colours so the chips read in light and dark. The dot is
// the same colour in the role menu.
const V3_ROLE_STYLES: Record<SpeakerRole, { chip: string; dot: string }> = {
  clinician: {
    chip: "bg-v3-brand-tint text-v3-brand-strong ring-v3-brand-tint-2",
    dot: "bg-v3-brand",
  },
  other_clinician: {
    chip: "bg-v3-panel text-v3-brand-strong ring-v3-brand-ring",
    dot: "bg-transparent ring-2 ring-inset ring-v3-brand",
  },
  patient: {
    chip: "bg-v3-ok-tint text-v3-ok-text ring-v3-ok-tint",
    dot: "bg-v3-ok",
  },
  caregiver: {
    chip: "bg-v3-warn-tint text-v3-warn-text ring-v3-warn-tint",
    dot: "bg-v3-warn",
  },
  interpreter: {
    chip: "bg-v3-surface-3 text-v3-text ring-v3-border-strong",
    dot: "bg-v3-ink",
  },
  other_patient: {
    chip: "bg-v3-rec-tint text-v3-rec-text ring-v3-rec-tint",
    dot: "bg-v3-rec",
  },
  other: {
    chip: "bg-v3-surface-2 text-v3-text-2 ring-v3-border-strong",
    dot: "bg-v3-text-3",
  },
  unknown: {
    chip: "bg-v3-surface-2 text-v3-text-2 ring-v3-border",
    dot: "bg-transparent ring-1 ring-inset ring-v3-text-3",
  },
};
const V3_UNKNOWN_CHIP = `font-v3 ${V3_ROLE_STYLES.unknown.chip}`;
// The clinician picks from these; "unknown" resets a wrong guess.
const MENU_ROLES = SPEAKER_ROLES;

export const useSpeakerLabel = () => {
  const { t } = useTranslation();
  return (speaker: number, assignment: SpeakerAssignment | null): string => {
    if (!assignment || assignment.role === "unknown")
      return t("visit.speakers.speakerNumber", { number: speaker + 1 });
    const role = t(`visit.speakers.roles.${assignment.role}`);
    return assignment.relation
      ? `${role} (${t(`visit.speakers.relations.${assignment.relation}`, { defaultValue: assignment.relation })})`
      : role;
  };
};

const SpeakerChip = ({
  speaker,
  assignment,
  recordingPublicId,
  editable,
}: {
  speaker: number;
  assignment: SpeakerAssignment | null;
  recordingPublicId: string | null;
  editable: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const speakers = useVisitSpeakers();
  const label = useSpeakerLabel()(speaker, assignment);
  const v3 = useTranscriptV3();
  const saving =
    speakers?.saving === `${recordingPublicId ?? "main"}:${speaker}`;
  const chip = cn(
    "relative mr-2 inline-flex h-6 min-h-0! items-center gap-1 rounded-full px-2.5 align-[1px] text-xs font-semibold ring-1 ring-inset",
    v3
      ? !assignment || assignment.role === "unknown"
        ? V3_UNKNOWN_CHIP
        : `font-v3 ${V3_ROLE_STYLES[assignment.role].chip}`
      : ROLE_STYLES[assignment?.role ?? "unset"],
  );
  if (!editable || !speakers?.setRole)
    return (
      <span className={chip} data-testid="speaker-chip">
        {label}
      </span>
    );
  const setRole = speakers.setRole;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={saving}>
        <button
          aria-label={t("visit.speakers.change", {
            label,
            number: speaker + 1,
          })}
          className={cn(
            chip,
            // 44px touch target without growing the chip inside the text.
            "cursor-pointer after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-[''] hover:brightness-95 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
          )}
          data-testid="speaker-chip"
          type="button"
        >
          {label}
          {saving ? (
            <Loader2 aria-hidden className="size-3 animate-spin" />
          ) : (
            <ChevronDown aria-hidden className="size-3 opacity-70" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn("w-56", v3 && V3_MENU_CONTENT)}
        data-testid="speaker-role-menu"
      >
        <DropdownMenuLabel
          className={
            v3 ? V3_MENU_LABEL : "text-xs font-medium text-muted-foreground"
          }
        >
          {t("visit.speakers.menuTitle", { number: speaker + 1 })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={v3 ? V3_MENU_SEPARATOR : undefined} />
        {MENU_ROLES.map((role) => (
          <DropdownMenuItem
            key={role}
            className={v3 ? V3_MENU_ITEM : "min-h-11 gap-2 lg:min-h-8"}
            onSelect={() => {
              if (role === assignment?.role) return;
              void setRole({
                recordingPublicId,
                speaker,
                role,
                // Keep a stated relation when the role stays caregiver.
                relation:
                  role === "caregiver" ? (assignment?.relation ?? null) : null,
              });
            }}
          >
            <span
              aria-hidden
              className={cn(
                "size-2.5 shrink-0 rounded-full",
                v3
                  ? V3_ROLE_STYLES[role].dot
                  : cn("ring-1 ring-inset", ROLE_STYLES[role]),
              )}
              data-role-dot={role}
            />
            <span className="flex-1">{t(`visit.speakers.roles.${role}`)}</span>
            {role === (assignment?.role ?? null) && (
              <Check
                aria-hidden
                className={cn("size-4", v3 && "text-v3-brand!")}
              />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// A transcript with speaker turns. Without speaker roles, or for a transcript
// without diarization labels, it renders the text as before.
export const SpeakerTranscript = ({
  text,
  recordingPublicId,
  editable = true,
}: {
  text: string | null | undefined;
  // null: the visit's first recording.
  recordingPublicId: string | null;
  // False for older versions: their labels are shown but not changed.
  editable?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const v3 = useTranscriptV3();
  const speakers = useVisitSpeakers();
  const turns = useMemo(() => parseSpeakerTurns(text ?? ""), [text]);
  // AI flags (VITE_TRANSCRIPT_FLAGS_ENABLED) apply to the latest transcript.
  const recordingFlags = useTranscriptFlagsFor(recordingPublicId);
  const flags = editable ? recordingFlags : [];
  const turnFlags = useMemo(() => {
    let cursor = 0;
    return turns.map((turn) => {
      const placed = flagsInTurn(text ?? "", turn.text, cursor, flags);
      cursor = placed.next;
      return placed.flags;
    });
  }, [turns, text, flags]);
  if (
    !AppConfig.enableSpeakerRoles ||
    !turns.some((turn) => turn.speaker !== null)
  )
    return flags.length ? (
      <p>
        <FlaggedText flags={flags} text={text ?? ""} />
      </p>
    ) : (
      <p>{text}</p>
    );
  const identifying =
    speakers?.identifying &&
    turns.some((turn) => turn.speaker !== null && !turn.assignment);
  return (
    <div className="space-y-3" data-testid="speaker-transcript">
      {identifying && (
        <p
          className="flex items-center gap-2 text-sm text-muted-foreground"
          role="status"
        >
          <Loader2 aria-hidden className="size-3.5 animate-spin" />
          {t("visit.speakers.identifying")}
        </p>
      )}
      {turns.map((turn, index) => (
        // Turns have no identity beyond their position in the transcript.
        // biome-ignore lint/suspicious/noArrayIndexKey: static order
        <p
          key={index}
          className={cn(
            "leading-relaxed",
            v3 && "font-v3 text-[15px] leading-[1.6] text-v3-text",
          )}
        >
          {turn.speaker !== null && (
            <SpeakerChip
              assignment={turn.assignment}
              editable={editable}
              recordingPublicId={recordingPublicId}
              speaker={turn.speaker}
            />
          )}
          <FlaggedText flags={turnFlags[index] ?? []} text={turn.text} />
        </p>
      ))}
    </div>
  );
};
